import Save16 from '@carbon/icons-react/es/save/16';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Radio } from 'components/atoms/radio';
import { Button } from 'components/facade/button';
import { Input } from 'components/facade/input';
import { SettingsControlPanel } from 'components/molecules/settings/control-panel';
import { useTranslate } from 'hooks/use-translation';
import { useUpdateSettings } from '../use-feature-setting';

const StyledSmallText = styled.div`
  font-size: var(--font-size-sm);
  color: var(--gray-70);
`;

export const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;
export const StyledCustomInputWrapper = styled.div`
  display: flex;
  gap: var(--spacing-03);
`;

interface Props {
  creditNoteTitle?: string;
}

const CUSTOM_KEY = 'custom';

export const CreditNoteTitle: FC<Props> = ({
  creditNoteTitle: defaultCreditNoteTitle,
}) => {
  const translate = useTranslate();
  const [update, isLoading] = useUpdateSettings();
  const [selectedKey, setSelectedKey] = useState<string | undefined>();
  const [customCreditNoteTitle, setCustomCreditNoteTitle] = useState('');

  const creditNoteTitleMap: Record<string, string> = useMemo(
    () => ({
      credit_note: translate('settings.credit_notes.credit_note_title.option1'),
      credit_memo: translate('settings.credit_notes.credit_note_title.option2'),
      [CUSTOM_KEY]: translate(
        'settings.credit_notes.credit_note_title.option3',
      ),
    }),
    [translate],
  );

  const onSave = (title: string) => {
    update({
      payload: {
        creditNoteDisplayTitle: title,
      },
      onError: () => {
        const entry = Object.entries(creditNoteTitleMap).find(
          ([key, value]) =>
            key !== CUSTOM_KEY && value === defaultCreditNoteTitle,
        );

        if (entry) {
          setSelectedKey(entry[0]);
        } else {
          setSelectedKey(CUSTOM_KEY);
          setCustomCreditNoteTitle(defaultCreditNoteTitle ?? '');
        }
      },
    });
  };

  useEffect(() => {
    const entry = Object.entries(creditNoteTitleMap).find(
      ([key, value]) => key !== CUSTOM_KEY && value === defaultCreditNoteTitle,
    );

    if (entry) {
      setSelectedKey(entry[0]);
    } else {
      setCustomCreditNoteTitle(defaultCreditNoteTitle ?? '');
      setSelectedKey(CUSTOM_KEY);
    }
  }, [creditNoteTitleMap, defaultCreditNoteTitle]);

  return (
    <SettingsControlPanel
      label={translate('settings.credit_notes.credit_note_title')}
      descriptions={[
        translate('settings.credit_notes.credit_note_title.description'),
      ]}
    >
      <StyledRadioGroup
        value={selectedKey}
        onChange={(e) => {
          const key = e.target.value;

          setSelectedKey(key);
          if (key !== CUSTOM_KEY) {
            onSave(creditNoteTitleMap[key]);
          }
        }}
      >
        {Object.entries(creditNoteTitleMap).map(([key, label]) => (
          <Radio value={key} key={key}>
            {label}
          </Radio>
        ))}
        {selectedKey === CUSTOM_KEY && (
          <>
            <StyledCustomInputWrapper>
              <Input
                value={customCreditNoteTitle}
                placeholder={translate(
                  'settings.credit_notes.credit_note_title.custom.placeholder',
                )}
                onChange={(e) => setCustomCreditNoteTitle(e.target.value)}
                maxLength={32}
              />
              <Button
                disabled={customCreditNoteTitle.length === 0}
                onClick={() => onSave(customCreditNoteTitle)}
                type="primary"
                icon={<Save16 />}
                loading={isLoading}
              >
                <FormattedMessage id="save" />
              </Button>
            </StyledCustomInputWrapper>
            <StyledSmallText>
              <FormattedMessage id="settings.credit_notes.credit_note_title.custom.max_length" />
            </StyledSmallText>
          </>
        )}
      </StyledRadioGroup>
    </SettingsControlPanel>
  );
};
